/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { ShiftCrossLocationOpen, ShiftsCrossLocationOpen } from "@microsoft/shifts-models-data";
// TODO: Replace 'sh-services' dependency from Web core with one from a workspace package.
import { NetworkService } from "sh-services";
import { DataProvider } from "sh-services/dataproviders/DataProvider";

import { CrossLocationOpenShiftDataProviderParams } from "./CrossLocationOpenShiftDataProviderParams";

/**
 * The cross-location open shift data provider.
 */
export class CrossLocationOpenShiftDataProvider extends DataProvider<ShiftCrossLocationOpen[]> {
    /**
     * Instantiates a new instance of the CrossLocationOpenShiftDataProvider.
     * @param params The parameters.
     */
    constructor(private readonly params: CrossLocationOpenShiftDataProviderParams) {
        super();
    }

    /**
     * Gets data from memory or undefined.
     * @returns The data if it's found in memory.
     */
    public async getDataFromMemory(): Promise<ShiftCrossLocationOpen[] | undefined> {
        // Memory only stores view models, so returning undefined.
        return Promise.resolve(undefined);
    }

    /**
     * Return data if it's found in the database (otherwise return undefined).
     * @returns The data if it's found in database.
     */
    public async getDataFromDatabase(): Promise<ShiftCrossLocationOpen[]> {
        const { crossLocationTeamId, database, teamId } = this.params;

        return await database.getCrossLocationOpenShifts(teamId, crossLocationTeamId);
    }

    /**
     * Gets data from network call.
     * @returns The data from the network call.
     */
    public async getDataFromNetwork(): Promise<ShiftCrossLocationOpen[]> {
        const { crossLocationTeamId, teamId, tenantId } = this.params;
        let url = `/api/tenants/${tenantId}/teams/${teamId}/shifts/crosslocationopen`;
        const urlSignature = "/api/tenants/{tenantId}/teams/{teamId}/shifts/crosslocationopen";

        url += `?teamids=${crossLocationTeamId}`;

        const { data } = await NetworkService.sendHttpRequestToStaffHubService<ShiftsCrossLocationOpen>({
            method: "GET",
            url,
            urlSignature
        });

        return data?.value ?? [];
    }

    /**
     * Sets data in memory.
     * @param data The data to set in memory.
     */
    public async setDataInMemory(data: ShiftCrossLocationOpen[]): Promise<void> {
        this.params.setDataInMemory(data);
    }

    /**
     * Sets data in database.
     * @param data The data to set in database.
     */
    public async setDataInDatabase(data: ShiftCrossLocationOpen[]): Promise<void> {
        if (data) {
            const { crossLocationTeamId, database, teamId } = this.params;

            await database.setCrossLocationOpenShifts(teamId, crossLocationTeamId, data);
        }
    }

    /**
     * Not supported.
     */
    public async saveDataToNetwork(): Promise<ShiftCrossLocationOpen[]> {
        return [];
    }
}
